import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { get, isNil } from 'lodash';
import { useDimension } from 'hooks';
import { ENTITY } from 'modules/campaigns/config/routes/entity';
import { useCampaignGroupEnabled } from 'modules/campaigns/modules/campaigns-group-form/hooks/useCampaignGroupEnabled';
import { addIndication, INDICATION_TYPES } from 'modules/taboola-common-frontend-modules/Indications';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { PERMISSIONS_OPERATORS } from 'modules/taboola-common-frontend-modules/authentication';
import usePermissions from 'modules/taboola-common-frontend-modules/authentication/hooks';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useCampaignGroupApi } from 'services/api';
import { AllCampaignGroupId } from '../../../../../config/allCampaignGroupId';
import { isOnlySponsored } from '../../../../../config/report-predicates/isOnlySponsored';
import { toGWtransformers } from '../../../../../flows/flowsUtils';
import { createCampaignsGroupSuccess } from '../../../../campaigns-group/actions';
import { campaignsGroupMapSelector } from '../../../../campaigns-group/selectors';

const errorIndication = {
    message: <FormattedMessage id="campaign.group.fetch.error" defaultMessage="Unable to fetch Campaign Group." />,
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="error.highlight" />,
};

export const useFetchedCampaignGroup = campaignGroupId => {
    const dispatch = useDispatch();
    const account = useAccount();
    const accountName = account?.accountName;
    const cache = useSelector(campaignsGroupMapSelector);
    const { getCampaignGroup } = useCampaignGroupApi();
    const isPermitted = usePermissions(['VIEW_CAMPAIGN_GROUP', 'EDIT_CAMPAIGN_GROUP'], PERMISSIONS_OPERATORS.OR);
    const [dimension] = useDimension();
    const campaignGroupExtensionEnabled = useCampaignGroupEnabled();
    const isConfigured = useMemo(
        () => campaignGroupExtensionEnabled && isOnlySponsored({ dimension, selectedAccount: account }),
        [account, campaignGroupExtensionEnabled, dimension]
    );
    const { data } = useQuery(
        ['CAMPAIGN_GROUP', accountName, campaignGroupId],
        async () => {
            const rawCampaignGroup = await getCampaignGroup(accountName, campaignGroupId);
            const campaignGroup = toGWtransformers[ENTITY.CAMPAIGNS_GROUP](rawCampaignGroup);
            dispatch(createCampaignsGroupSuccess(campaignGroup));
            return campaignGroup;
        },
        {
            enabled:
                !isNil(campaignGroupId) &&
                Number(campaignGroupId) !== AllCampaignGroupId &&
                isPermitted &&
                isConfigured,
            onError: () => dispatch(addIndication(errorIndication)),
        }
    );

    const campaignGroup = data ?? get(cache, campaignGroupId);

    if (campaignGroupId === AllCampaignGroupId) {
        return {
            campaignsGroupId: AllCampaignGroupId,
            campaignsGroupName: `app.campaigns.all.${ENTITY.CAMPAIGNS_GROUP}`,
        };
    }

    return campaignGroup;
};
